import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import PersonIcon from '@mui/icons-material/Person';
import TimerIcon from '@mui/icons-material/Timer';
import { Box, Grid, IconButton, Tooltip, Typography, TableSortLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import FileListItem from './FileListItem';
import FolderListItem from './FolderListItem';

const ListView = ({ folders, files, onFolderClick, onFileClick, onMenuOpen, showPaths = false }) => {
    const theme = useTheme(); // Get current theme to check dark mode
    const hasItems = (folders && folders.length > 0) || (files && files.length > 0);
    
    // Add state for sorting
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    
    // Handle sort request
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    
    // Sort function for folders
    const sortedFolders = React.useMemo(() => {
        if (!folders || folders.length === 0) return [];
        
        return [...folders].sort((a, b) => {
            // Always sort folders by the selected criteria
            switch (orderBy) {
                case 'name':
                    return order === 'asc' 
                        ? a.name.localeCompare(b.name) 
                        : b.name.localeCompare(a.name);
                case 'updatedBy':
                    const updatedByA = a.updatedBy || 'Unknown';
                    const updatedByB = b.updatedBy || 'Unknown';
                    return order === 'asc' 
                        ? updatedByA.localeCompare(updatedByB) 
                        : updatedByB.localeCompare(updatedByA);
                case 'updatedAt':
                    const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    return order === 'asc' ? dateA - dateB : dateB - dateA;
                default:
                    return 0;
            }
        });
    }, [folders, order, orderBy]);
    
    // Sort function for files
    const sortedFiles = React.useMemo(() => {
        if (!files || files.length === 0) return [];
        
        return [...files].sort((a, b) => {
            switch (orderBy) {
                case 'name':
                    const nameA = a.name || a.videoName || '';
                    const nameB = b.name || b.videoName || '';
                    return order === 'asc' 
                        ? nameA.localeCompare(nameB) 
                        : nameB.localeCompare(nameA);
                case 'duration':
                    // Parse duration format "HH:MM:SS" to seconds for comparison
                    const getDurationInSeconds = (duration) => {
                        if (!duration) return 0;
                        const parts = duration.split(':').map(Number);
                        if (parts.length === 3) { // HH:MM:SS
                            return parts[0] * 3600 + parts[1] * 60 + parts[2];
                        } else if (parts.length === 2) { // MM:SS
                            return parts[0] * 60 + parts[1];
                        }
                        return 0;
                    };
                    
                    const durationA = getDurationInSeconds(a.duration);
                    const durationB = getDurationInSeconds(b.duration);
                    return order === 'asc' ? durationA - durationB : durationB - durationA;
                case 'updatedBy':
                    const updatedByA = a.updatedBy || a.lastModifiedBy || 'Unknown';
                    const updatedByB = b.updatedBy || b.lastModifiedBy || 'Unknown';
                    return order === 'asc' 
                        ? updatedByA.localeCompare(updatedByB) 
                        : updatedByB.localeCompare(updatedByA);
                case 'updatedAt':
                    const dateA = a.updatedAt || a.lastModifiedDate 
                        ? new Date(a.updatedAt || a.lastModifiedDate).getTime() 
                        : 0;
                    const dateB = b.updatedAt || b.lastModifiedDate 
                        ? new Date(b.updatedAt || b.lastModifiedDate).getTime() 
                        : 0;
                    return order === 'asc' ? dateA - dateB : dateB - dateA;
                case 'languages':
                    // Sort by number of languages
                    const langCountA = (a.languages?.length || 0);
                    const langCountB = (b.languages?.length || 0);
                    return order === 'asc' ? langCountA - langCountB : langCountB - langCountA;
                default:
                    return 0;
            }
        });
    }, [files, order, orderBy]);

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Help Overlay */}
            
            {/* Column headers - Only show if we have items */}
            {hasItems && (
                <Grid
                    container
                    sx={{
                        px: 1,
                        py: 0.5,
                        mb: 0.5,
                        backgroundColor: 'background.paper',
                        borderRadius: '8px',
                        display: { xs: 'none', sm: 'flex' }, // Hide on mobile
                        // Apply theme-aware color
                        color: 'text.primary',
                    }}
                >
                    <Grid item xs={12} sm={4} md={4}>
                        <TableSortLabel
                            active={orderBy === 'name'}
                            direction={orderBy === 'name' ? order : 'asc'}
                            onClick={() => handleRequestSort('name')}
                            sx={{ pl: 5 }}
                        >
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                Name
                            </Typography>
                        </TableSortLabel>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    Details
                                </Box>
                            </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2} md={1}>
                        <TableSortLabel
                            active={orderBy === 'duration'}
                            direction={orderBy === 'duration' ? order : 'asc'}
                            onClick={() => handleRequestSort('duration')}
                        >
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <TimerIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                    Duration
                                </Box>
                            </Typography>
                        </TableSortLabel>
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                        <TableSortLabel
                            active={orderBy === 'updatedBy'}
                            direction={orderBy === 'updatedBy' ? order : 'asc'}
                            onClick={() => handleRequestSort('updatedBy')}
                        >
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                    Last Updated By
                                </Box>
                            </Typography>
                        </TableSortLabel>
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                        <TableSortLabel
                            active={orderBy === 'updatedAt'}
                            direction={orderBy === 'updatedAt' ? order : 'asc'}
                            onClick={() => handleRequestSort('updatedAt')}
                        >
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <AccessTimeFilledIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                    Last Updated At
                                </Box>
                            </Typography>
                        </TableSortLabel>
                    </Grid>
                    <Grid item xs={6} sm={2} md={1}>
                        <Typography variant="subtitle2" align="center" sx={{ color: 'text.primary' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                Actions
                            </Box>
                        </Typography>
                    </Grid>
                </Grid>
            )}

            {/* Folders section */}
            {folders && folders.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                        Folders {folders.length > 0 && `(${folders.length})`}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {sortedFolders.map((folder, index) => (
                            <FolderListItem
                                key={`folder-${index}`}
                                className="file-folder-item"
                                folder={folder}
                                onFolderClick={onFolderClick}
                                onMenuOpen={onMenuOpen}
                                showPath={showPaths}
                                isDarkMode={theme.palette.mode === 'dark'}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Files section */}
            {files && files.length > 0 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                        Files {files.length > 0 && `(${files.length})`}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {sortedFiles.map((file, index) => (
                            <FileListItem
                                key={`file-${index}`}
                                className="file-folder-item"
                                file={file}
                                onFileClick={onFileClick}
                                onMenuOpen={onMenuOpen}
                                showPath={showPaths}
                                isDarkMode={theme.palette.mode === 'dark'}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Empty state */}
            {!hasItems && (
                <Box
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                        This folder is empty
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Create a new folder or upload files to get started
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ListView;